import { useState, useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { authenticator } from 'otplib';
import QRCode from 'qrcode';
import { supabase } from '../lib/supabase';

export interface User2FA {
  id: string;
  email: string;
  secret: string;
  backup_codes: string[];
  is_enabled: boolean;
  created_at: string;
  updated_at: string;
}

export interface TwoFactorSetup {
  secret: string;
  qrCodeUrl: string;
  backupCodes: string[];
}

export function use2FA() {
  const { user, isAuthenticated } = useAuth0();
  const [is2FAEnabled, setIs2FAEnabled] = useState<boolean | null>(null);
  const [loading, setLoading] = useState(true);
  const [setupData, setSetupData] = useState<TwoFactorSetup | null>(null);

  // Generate backup codes
  const generateBackupCodes = (): string[] => {
    const codes = [];
    for (let i = 0; i < 10; i++) {
      codes.push(Math.random().toString(36).substring(2, 10).toUpperCase());
    }
    return codes;
  };

  // Generate a browser-compatible TOTP secret
  const generateSecretBrowserSafe = (): string => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ234567'; // Base32 characters
    let secret = '';
    for (let i = 0; i < 32; i++) {
      secret += chars[Math.floor(Math.random() * chars.length)];
    }
    return secret;
  };

  // Check if user has 2FA enabled
  const check2FAStatus = async () => {
    if (!user?.email) return;

    try {
      // Set the current user email for RLS policy
      await supabase.rpc('set_config', {
        setting_name: 'app.current_user_email',
        setting_value: user.email,
        is_local: true
      });

      const { data, error } = await supabase
        .from('user_2fa')
        .select('is_enabled')
        .eq('email', user.email)
        .single();

      if (error && error.code !== 'PGRST116') { // PGRST116 = no rows returned
        throw error;
      }

      setIs2FAEnabled(data?.is_enabled || false);
    } catch (error) {
      console.error('Error checking 2FA status:', error);
      setIs2FAEnabled(false);
    } finally {
      setLoading(false);
    }
  };

  // Initialize 2FA setup
  const initialize2FA = async (): Promise<TwoFactorSetup> => {
    if (!user?.email) throw new Error('User not authenticated');

    const secret = generateSecretBrowserSafe(); // Use our browser-compatible function
    const backupCodes = generateBackupCodes();
    
    // Generate service name and account name for QR code
    const serviceName = 'Payinthi DMS';
    const accountName = user.email;
    
    // Create otpauth URL
    const otpauthUrl = authenticator.keyuri(accountName, serviceName, secret);
    
    // Generate QR code
    const qrCodeUrl = await QRCode.toDataURL(otpauthUrl);

    const setupData = {
      secret,
      qrCodeUrl,
      backupCodes
    };

    setSetupData(setupData);
    return setupData;
  };

  // Verify TOTP code
  // Browser-compatible TOTP verification using Web Crypto API
  const verifyTOTPBrowser = async (token: string, secret: string): Promise<boolean> => {
    try {
      // Base32 decode
      const base32Chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ234567';
      let bits = '';
      for (let i = 0; i < secret.length; i++) {
        const val = base32Chars.indexOf(secret[i].toUpperCase());
        if (val === -1) throw new Error('Invalid base32 character');
        bits += val.toString(2).padStart(5, '0');
      }
      
      // Convert bits to bytes
      const bytes = new Uint8Array(Math.floor(bits.length / 8));
      for (let i = 0; i < bytes.length; i++) {
        bytes[i] = parseInt(bits.substr(i * 8, 8), 2);
      }
      
      // Get current time step (30-second intervals)
      const currentTime = Math.floor(Date.now() / 1000 / 30);
      
      // Try current time step and ±1 time steps for clock drift tolerance
      const timeSteps = [currentTime - 1, currentTime, currentTime + 1];
      
      for (const timeStep of timeSteps) {
        // Create time buffer (8 bytes, big endian)
        const timeBuffer = new ArrayBuffer(8);
        const timeView = new DataView(timeBuffer);
        timeView.setUint32(4, timeStep, false); // big endian
        
        // Import secret key
        const key = await crypto.subtle.importKey(
          'raw',
          bytes,
          { name: 'HMAC', hash: 'SHA-1' },
          false,
          ['sign']
        );
        
        // Generate HMAC
        const signature = await crypto.subtle.sign('HMAC', key, timeBuffer);
        const hmac = new Uint8Array(signature);
        
        // Dynamic truncation
        const offset = hmac[hmac.length - 1] & 0x0f;
        const code = (
          ((hmac[offset] & 0x7f) << 24) |
          ((hmac[offset + 1] & 0xff) << 16) |
          ((hmac[offset + 2] & 0xff) << 8) |
          (hmac[offset + 3] & 0xff)
        ) % 1000000;
        
        // Compare with provided token
        const codeString = code.toString().padStart(6, '0');
        
        if (codeString === token) {
          return true;
        }
      }
      
      return false;
    } catch (error) {
      console.error('Browser TOTP verification error:', error);
      return false;
    }
  };

  const verifyCode = async (token: string, secret?: string): Promise<boolean> => {
    const secretToUse = secret || setupData?.secret;
    if (!secretToUse) {
      console.error('No secret available for verification');
      return false;
    }

    try {
      // Use our browser-compatible verification
      const result = await verifyTOTPBrowser(token, secretToUse);
      return result;
    } catch (error) {
      console.error('Error verifying TOTP code:', error);
      return false;
    }
  };

  // Enable 2FA after verification
  const enable2FA = async (verificationCode: string, secretToVerify?: string, backupCodesToSave?: string[]): Promise<{ success: boolean; error?: string }> => {
    if (!user?.email) {
      return { success: false, error: 'User not authenticated' };
    }

    // Use provided secret or fall back to setupData
    const secret = secretToVerify || setupData?.secret;
    const backupCodes = backupCodesToSave || setupData?.backupCodes || [];
    
    if (!secret) {
      return { success: false, error: 'Setup data not found' };
    }

    // Verify the code first
    const isValid = await verifyCode(verificationCode, secret);
    if (!isValid) {
      return { success: false, error: 'Invalid verification code' };
    }

    try {
      // Set the current user email for RLS policy
      await supabase.rpc('set_config', {
        setting_name: 'app.current_user_email',
        setting_value: user.email,
        is_local: true
      });

      const { error } = await supabase
        .from('user_2fa')
        .upsert({
          email: user.email,
          secret: secret,
          backup_codes: backupCodes,
          is_enabled: true
        });

      if (error) throw error;

      setIs2FAEnabled(true);
      setSetupData(null);
      return { success: true };
    } catch (error) {
      console.error('Error enabling 2FA:', error);
      return { success: false, error: 'Failed to enable 2FA' };
    }
  };

  // Disable 2FA
  const disable2FA = async (verificationCode: string): Promise<{ success: boolean; error?: string }> => {
    if (!user?.email) {
      return { success: false, error: 'User not authenticated' };
    }

    try {
      // Get user's secret first
      const { data: userData, error: fetchError } = await supabase
        .from('user_2fa')
        .select('secret')
        .eq('email', user.email)
        .single();

      if (fetchError) throw fetchError;

      // Verify the code
      const isValid = await verifyCode(verificationCode, userData.secret);
      if (!isValid) {
        return { success: false, error: 'Invalid verification code' };
      }

      // Delete 2FA record
      const { error } = await supabase
        .from('user_2fa')
        .delete()
        .eq('email', user.email);

      if (error) throw error;

      setIs2FAEnabled(false);
      return { success: true };
    } catch (error) {
      console.error('Error disabling 2FA:', error);
      return { success: false, error: 'Failed to disable 2FA' };
    }
  };

  // Verify 2FA for login
  const verify2FAForLogin = async (code: string): Promise<{ success: boolean; error?: string }> => {
    if (!user?.email) {
      return { success: false, error: 'User not authenticated' };
    }

    try {
      // Set the current user email for RLS policy
      await supabase.rpc('set_config', {
        setting_name: 'app.current_user_email',
        setting_value: user.email,
        is_local: true
      });

      const { data, error } = await supabase
        .from('user_2fa')
        .select('secret, backup_codes')
        .eq('email', user.email)
        .eq('is_enabled', true)
        .single();

      if (error) throw error;

      // Try TOTP first
      const isValidTOTP = await verifyCode(code, data.secret);
      if (isValidTOTP) {
        return { success: true };
      }

      // Check backup codes
      if (data.backup_codes && data.backup_codes.includes(code.toUpperCase())) {
        // Remove used backup code
        const updatedCodes = data.backup_codes.filter((c: string) => c !== code.toUpperCase());
        
        await supabase
          .from('user_2fa')
          .update({ backup_codes: updatedCodes })
          .eq('email', user.email);

        return { success: true };
      }

      return { success: false, error: 'Invalid code' };
    } catch (error) {
      console.error('Error verifying 2FA:', error);
      return { success: false, error: 'Verification failed' };
    }
  };

  // Generate new backup codes
  const regenerateBackupCodes = async (): Promise<{ success: boolean; backupCodes?: string[]; error?: string }> => {
    if (!user?.email) {
      return { success: false, error: 'User not authenticated' };
    }

    try {
      const newBackupCodes = generateBackupCodes();

      // Set the current user email for RLS policy
      await supabase.rpc('set_config', {
        setting_name: 'app.current_user_email',
        setting_value: user.email,
        is_local: true
      });

      const { error } = await supabase
        .from('user_2fa')
        .update({ backup_codes: newBackupCodes })
        .eq('email', user.email);

      if (error) throw error;

      return { success: true, backupCodes: newBackupCodes };
    } catch (error) {
      console.error('Error regenerating backup codes:', error);
      return { success: false, error: 'Failed to regenerate backup codes' };
    }
  };

  useEffect(() => {
    if (isAuthenticated && user?.email) {
      check2FAStatus();
    }
  }, [isAuthenticated, user?.email]);

  return {
    is2FAEnabled,
    loading,
    setupData,
    initialize2FA,
    verifyCode,
    enable2FA,
    disable2FA,
    verify2FAForLogin,
    regenerateBackupCodes
  };
}